/**
 * Трассировка старта игры для отладки зависаний на реальных устройствах.
 *
 * Проблема, ради которой существует: на части планшетов/телефонов приложение
 * намертво зависает в момент создания Phaser-игры, и ни консоль, ни dev-оверлей
 * ошибок посмотреть уже нельзя (на проде оверлея нет вовсе). Каждая веха
 * пишется СИНХРОННО в localStorage и в оверлей поверх страницы: даже после
 * полного зависания и перезагрузки хвост предыдущего запуска показывается
 * сверху — по последней записанной строке видно, на каком шаге все умерло.
 *
 * Включение: открыть приложение с ?bootTrace=1 — флаг запоминается в
 * localStorage и действует дальше и в PWA, где query-параметра нет.
 * Выключение: ?bootTrace=0. Без флага не делает ничего.
 */

const STORAGE_KEY_LOG = 'strategikonBootTraceLog';
const STORAGE_KEY_ENABLED = 'strategikonBootTraceEnabled';
const MAX_LINES = 40;
const PREV_TAIL_LINES = 6;

// localStorage может быть недоступен (Safari в приватном режиме) — тогда
// трассер продолжает работать одним оверлеем, без памяти между запусками
const readStorage = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const writeStorage = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch (e) {
    // работаем без localStorage
  }
};

const removeStorage = (key) => {
  try {
    window.localStorage.removeItem(key);
  } catch (e) {
    // работаем без localStorage
  }
};

let cachedEnabled = null;
let lines = [];
let prevTail = [];
let overlayEl = null;

export const isBootTraceEnabled = () => {
  if (cachedEnabled === null) {
    const search = window.location.search;
    if (search.includes('bootTrace=1')) {
      writeStorage(STORAGE_KEY_ENABLED, '1');
      cachedEnabled = true;
    } else if (search.includes('bootTrace=0')) {
      removeStorage(STORAGE_KEY_ENABLED);
      cachedEnabled = false;
    } else {
      cachedEnabled = readStorage(STORAGE_KEY_ENABLED) === '1';
    }
  }
  return cachedEnabled;
};

const ensureOverlay = () => {
  if (overlayEl || !document.body) return;
  overlayEl = document.createElement('div');
  overlayEl.style.cssText = [
    'position: fixed',
    'left: 0',
    'bottom: 0',
    'z-index: 100000',
    'max-width: 100vw',
    'max-height: 45vh',
    'overflow: hidden',
    'padding: 4px 6px',
    'background: rgba(0, 0, 0, 0.75)',
    'color: #7cfc00',
    'font: 10px/1.35 monospace',
    'text-align: left',
    'white-space: pre-wrap',
    'pointer-events: none',
  ].join(';');
  document.body.appendChild(overlayEl);
};

const renderOverlay = () => {
  ensureOverlay();
  if (!overlayEl) return;
  const prevBlock = prevTail.length
    ? `── прошлый запуск ──\n${prevTail.join('\n')}\n── этот запуск ──\n`
    : '';
  overlayEl.textContent = prevBlock + lines.join('\n');
};

/**
 * Записывает веху: секунды от старта страницы + метка.
 * Вызов с выключенным флагом — no-op, дешевый настолько, что расставлять
 * вехи можно и в горячих местах.
 */
export const bootTrace = (label) => {
  if (!isBootTraceEnabled()) return;
  lines.push(`${(performance.now() / 1000).toFixed(1)} ${label}`);
  if (lines.length > MAX_LINES) lines = lines.slice(-MAX_LINES);
  writeStorage(STORAGE_KEY_LOG, lines.join('\n'));
  renderOverlay();
};

/**
 * Один раз при старте приложения (см. index.js): лог прошлого запуска
 * перекладывается в «хвост» оверлея, глобальные необработанные ошибки
 * начинают попадать в трассу.
 */
export const initBootTrace = () => {
  if (!isBootTraceEnabled()) return;

  const prev = readStorage(STORAGE_KEY_LOG);
  prevTail = prev ? prev.split('\n').slice(-PREV_TAIL_LINES) : [];
  removeStorage(STORAGE_KEY_LOG);

  window.addEventListener('error', (event) => {
    bootTrace(`ERROR ${event.message || event.type}`);
  });
  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    bootTrace(`REJECTION ${(reason && reason.message) || String(reason)}`);
  });

  bootTrace(`start ${window.innerWidth}x${window.innerHeight} dpr=${window.devicePixelRatio}`);
};
